import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, FileSpreadsheet, CalendarDays, Grid3x3, User, Users, TrendingUp } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Forecast } from "@/components/obboard/Forecast";
import { ShareBoardDialog } from "@/components/obboard/ShareBoardDialog";
import { Share2, Eye } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { isManager } from "@/lib/access";
import MarkPaidDialog from "@/components/MarkPaidDialog";
import { downloadFile } from "@/components/InvoiceRegister";
import { BoardFilters, Legend } from "@/components/obboard/BoardFilters";
import { MonthCalendar } from "@/components/obboard/MonthCalendar";
import { MonthSummary } from "@/components/obboard/MonthSummary";
import { YearGrid } from "@/components/obboard/YearGrid";
import { fmtDate } from "@/lib/format";

const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
const params = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));

const Toggle = ({ value, onChange, options, testid }) => (
  <div className="inline-flex bg-slate-100 rounded-md p-1" data-testid={testid}>
    {options.map(([v, label, Icon]) => (
      <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-colors ${value === v ? "bg-white shadow-sm text-blue-700 font-semibold" : "text-slate-600 hover:text-blue-700"}`}><Icon className="w-3.5 h-3.5" />{label}</button>
    ))}
  </div>
);

export default function ObligationsBoard() {
  const { user } = useAuth();
  const mgr = isManager(user);
  const [sp] = useSearchParams();
  const [view, setView] = useState(["month", "year", "forecast"].includes(sp.get("view")) ? sp.get("view") : "month");
  const [month, setMonth] = useState(nowMonth());
  const [f, setF] = useState({ scope: mgr ? "team" : "mine", entity: "all", category: "all", payment_mode: "all" });
  const [d, setD] = useState(null);
  const [pay, setPay] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [sharedBy, setSharedBy] = useState(sp.get("shared_by") || "");
  useEffect(() => { api.get("/obligations-board/shared-with-me").then((r) => setSharedWithMe(r.data)).catch(() => {}); }, []);
  const sharedOwner = sharedWithMe.find((s) => s.owner_id === sharedBy);
  const year = Number(month.slice(0, 4));
  const load = useCallback(async () => {
    try {
      const url = `/obligations-board/${view}`;
      const r = await api.get(url, { params: { ...params(f), ...(sharedBy ? { shared_by: sharedBy } : {}), ...(view === "month" ? { month } : view === "year" ? { year } : {}) } });
      setD({ ...r.data, _view: view });
    } catch (e) { toast.error(formatErr(e)); }
  }, [view, month, year, f, sharedBy]);
  useEffect(() => { setD(null); load(); }, [load]);
  const pick = (it) => {
    if (it.status === "paid") return toast.info(`${it.name}: paid on ${fmtDate(it.paid_on)}`);
    if (sharedOwner?.access === "view") return toast.info("View-only access: you can't mark payments paid on this board");
    if (!it.payable) return toast.info(`${it.name}: mark the earlier period (${fmtDate(it.obligation.next_due_date)}) paid first`);
    setPay(it.obligation);
  };
  return (
    <div data-testid="obligations-board-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Obligations</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Obligations board</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sharedWithMe.length > 0 && (
            <Select value={sharedBy || "own"} onValueChange={(v) => { setSharedBy(v === "own" ? "" : v); setF((x) => ({ ...x, entity: "all", category: "all" })); }}>
              <SelectTrigger data-testid="ob-board-owner" className="h-9 w-auto min-w-[190px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="own">My board</SelectItem>
                {sharedWithMe.map((s) => <SelectItem key={s.owner_id} value={s.owner_id}>{s.owner_name}'s team board</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {mgr && !sharedBy && <Button data-testid="ob-share-btn" variant="outline" onClick={() => setShareOpen(true)} className="gap-2 bg-white"><Share2 className="w-4 h-4 text-blue-700" />Share</Button>}
          {mgr && !sharedBy && <Toggle testid="ob-scope" value={f.scope} onChange={(v) => setF((x) => ({ ...x, scope: v }))} options={[["mine", "Mine", User], ["team", "Team", Users]]} />}
          <Toggle testid="ob-view" value={view} onChange={setView} options={[["month", "Month", CalendarDays], ["year", "Year", Grid3x3], ["forecast", "Forecast", TrendingUp]]} />
          <Button data-testid="ob-export-xlsx" variant="outline" className="gap-2 bg-white" onClick={() => downloadFile("/obligations-board/year.xlsx", { ...params(f), ...(sharedBy ? { shared_by: sharedBy } : {}), year }, `obligations-calendar-${year}.xlsx`)}>
            <FileSpreadsheet className="w-4 h-4 text-emerald-700" />Excel ({year})
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {view === "forecast" ? <div className="font-semibold text-slate-900" data-testid="ob-period-label">Next 3 months</div> : <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" data-testid="ob-prev" onClick={() => setMonth(shift(month, view === "month" ? -1 : -12))} className="h-9 w-9 bg-white"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="min-w-[160px] text-center font-semibold text-slate-900" data-testid="ob-period-label">{view === "month" ? monthLabel(month) : `January – December ${year}`}</div>
          <Button size="icon" variant="outline" data-testid="ob-next" onClick={() => setMonth(shift(month, view === "month" ? 1 : 12))} className="h-9 w-9 bg-white"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" data-testid="ob-today" onClick={() => setMonth(nowMonth())} className="text-blue-700">Today</Button>
        </div>}
        <BoardFilters f={f} setF={setF} facets={d?.facets} />
      </div>
      {sharedOwner && (
        <div data-testid="ob-shared-banner" className="flex items-center gap-2 text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
          <Eye className="w-4 h-4" />You're viewing <b>{sharedOwner.owner_name}'s</b> team board, shared with you. {sharedOwner.access === "view" ? "View only." : "You can mark payments paid here."}
        </div>
      )}
      <Legend />
      {!d || d._view !== view ? <div className="text-sm text-slate-500" data-testid="ob-board-loading">Loading…</div> : view === "month" ? (
        <div className="grid xl:grid-cols-[1fr_300px] gap-4">
          <MonthCalendar month={month} items={d.items} onPick={pick} />
          <MonthSummary totals={d.totals} balance={d.balance_required} />
        </div>
      ) : view === "forecast" ? <Forecast data={d} /> : (
        <YearGrid data={d} onMonth={(m) => { setMonth(`${year}-${String(m).padStart(2, "0")}`); setView("month"); }} />
      )}
      <ShareBoardDialog open={shareOpen} onClose={() => setShareOpen(false)} />
      <MarkPaidDialog open={!!pay} onOpenChange={(v) => !v && setPay(null)} obligation={pay} onDone={() => { setPay(null); load(); }} />
    </div>
  );
}
